import React, { useContext, useRef } from 'react'
import { SearchIndexContext } from '../../contexts/SearchIndexContext/SearchIndexContext'
import { InputAdornment, TextField, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import _ from 'lodash'
import SearchIcon from '@material-ui/icons/Search'
import InfoIcon from '@material-ui/icons/Info'
import { useIntl } from 'react-intl'
import { SearchResultsContext } from '../../contexts/SearchResultsContext/SearchResultsContext'
import {
  INDEX_COMMENT_TYPE,
  INDEX_INVESTIBLE_TYPE,
} from '../../contexts/SearchIndexContext/searchIndexContextMessages'
import { getComment, getCommentRoot } from '../../contexts/CommentsContext/commentsContextHelper'
import { CommentsContext } from '../../contexts/CommentsContext/CommentsContext'
import CloseIcon from '@material-ui/icons/Close';
import { getMarket } from '../../contexts/MarketsContext/marketsContextHelper'
import { MarketsContext } from '../../contexts/MarketsContext/MarketsContext'
import { getGroup } from '../../contexts/MarketGroupsContext/marketGroupsContextHelper';
import { MarketGroupsContext } from '../../contexts/MarketGroupsContext/MarketGroupsContext';
import { getInvestibleName } from '../../contexts/InvestibesContext/investiblesContextHelper';
import { InvestiblesContext } from '../../contexts/InvestibesContext/InvestiblesContext';
import { getInboxTarget } from '../../contexts/NotificationsContext/notificationsContextHelper';
import PropTypes from 'prop-types';
import { getSearchResults } from '../../contexts/SearchIndexContext/searchIndexContextHelper';
import { useHistory } from 'react-router';
import { getUrlForTicketCode, navigate } from '../../utils/marketIdPathFunctions';
import { TicketIndexContext } from '../../contexts/TicketContext/TicketIndexContext';

function SearchBox(props) {
  const { disableSearch = false, marketId, investibleId, action, groupId } = props;
  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.type === 'dark';
  const intl = useIntl();
  const history = useHistory();
  const [index] = useContext(SearchIndexContext);
  const [searchResults, setSearchResults] = useContext(SearchResultsContext);
  const [ticketState] = useContext(TicketIndexContext);
  const [commentsState] = useContext(CommentsContext);
  const [marketsState] = useContext(MarketsContext);
  const [groupState] = useContext(MarketGroupsContext);
  const [investibleState] = useContext(InvestiblesContext);
  const inputRef = React.useRef(null);
  const timeoutRef = useRef(null);
  let searchedName = undefined;
  if (investibleId) {
    searchedName = getInvestibleName(investibleState, investibleId);
  } else if (action !== 'groupEdit' && marketId && groupId) {
    const group = getGroup(groupState, marketId, groupId) || {};
    searchedName = group.name;
  } else if (getInboxTarget().includes(action)) {
    searchedName = intl.formatMessage({id: 'inbox'});
  }
  if (searchedName && searchedName.length > 40) {
    const lastIndex = searchedName.lastIndexOf(' ', 40);
    searchedName = `${searchedName.substring(0, lastIndex)}...`
  }

  function getInvestibleParents(result) {
    const parentResults = [];
    const parentMarket = getMarket(marketsState, result.marketId) || {}; //Protect against corrupt or loading
    const { parent_comment_id: inlineParentCommentId, parent_comment_market_id: parentMarketId } = parentMarket;
    if (inlineParentCommentId) {
      const comment = getComment(commentsState, parentMarketId, inlineParentCommentId);
      if (!comment) {
        // Parent comment was deleted
        return parentResults;
      }
      // Need to push this market because it is not in the results
      parentResults.push(parentMarketId);
      parentResults.push(inlineParentCommentId);
      if (comment.investible_id) {
        parentResults.push(comment.investible_id);
      }
    }
    return parentResults;
  }

  function getCommentParents(result) {
    const parentResults = [];
    const rootComment = getCommentRoot(commentsState, result.marketId, result.id);
    if (!rootComment) {
      // Somewhere on path to root a parent was deleted
      return undefined;
    }
    if (rootComment.id !== result.id) {
      parentResults.push(rootComment.id);
    }
    if (rootComment.investible_id) {
      parentResults.push(rootComment.investible_id);
      parentResults.push(...getInvestibleParents({id: rootComment.investible_id, marketId: result.marketId}));
    }
    return parentResults;
  }

  function getParentResults(results) {
    const parentResults = [];
    const removed = [];
    results.forEach((result) => {
      if (result.type === INDEX_COMMENT_TYPE) {
        const parents = getCommentParents(result);
        if (!parents) {
          removed.push(result);
        } else {
          parentResults.push(...parents);
        }
      } else if (result.type === INDEX_INVESTIBLE_TYPE) {
        parentResults.push(...getInvestibleParents(result));
      }
    });
    return {parentResults, removed};
  }

  function updateIndex(searchQuery){
    // A bare short code (e.g. Q-all-156) jumps straight to that item instead of text searching -
    // restores short-code search (T-all-2214). Only navigates when the code resolves to a real
    // ticket, so ordinary text queries fall through to the index search below.
    const ticketUrl = getUrlForTicketCode(searchQuery, ticketState, marketsState, commentsState);
    if (ticketUrl) {
      navigate(history, ticketUrl);
      return;
    }
    // query the index
    const rawResults = getSearchResults(index, searchQuery) || [];
    // parents in a different hash so they can appear on the page but not be counted as results
    const {parentResults, removed} = getParentResults(rawResults);
    const results = rawResults.filter((result) => !removed.find((item) => item.id === result.id));
    setSearchResults({
      search: searchQuery,
      results,
      parentResults
    });
  }

  function onSearchChange (event) {
    const { value } = event.target;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(function () {
      updateIndex(value);
    }, 2000);
  }

  function clearSearch() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setSearchResults({
      search: '',
      results: [],
      parentResults: [],
    });
  }

  return (
    <div id='search-box' onClick={(event) => event.stopPropagation()}
         style={{maxWidth: mobileLayout ? undefined : '50rem', display: 'flex', alignItems: 'center'}}>
      <TextField
        variant="outlined"
        style={{width: '100%', minWidth: mobileLayout ? '5rem' : '30rem'}}
        onChange={onSearchChange}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            updateIndex(ev.target.value);
            ev.preventDefault();
          }
        }}
        disabled={disableSearch}
        inputRef={inputRef}
        placeholder={`${intl.formatMessage({ id: mobileLayout ? 'searchBoxPlaceholderMobile' 
            : 'searchBoxPlaceholder' })} ${mobileLayout || _.isEmpty(searchedName) ? '' : searchedName}`}
        size="small"
        defaultValue={searchResults.search}
        InputProps={{
          style: { borderRadius: '8px', backgroundColor: isDark ? undefined : 'white' },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            _.isEmpty(searchResults.search) ? undefined :
            <InputAdornment style={{cursor: 'pointer'}}
                            onClick={clearSearch} position="end">
              <CloseIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      {!mobileLayout && (
        <Tooltip title={intl.formatMessage({ id: 'searchInfoTooltip' })}>
          <InfoIcon color='primary' style={{marginLeft: '0.35rem'}} />
        </Tooltip>
      )}
    </div>
  );
}

SearchBox.propTypes = {
  disableSearch: PropTypes.bool,
  marketId: PropTypes.string,
  investibleId: PropTypes.string,
  action: PropTypes.string,
  groupId: PropTypes.string
}

export default SearchBox;
